import React, { useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkCjkFriendly from 'remark-cjk-friendly';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; // KaTeX 스타일
import '../../styles/enhanced-markdown/base.css';
import '../../styles/enhanced-markdown/tables.css';
import '../../styles/enhanced-markdown/highlights.css';
import '../../styles/enhanced-markdown/reading.css';
import '../../styles/enhanced-markdown/parts.css';
import { MarkdownStreamingContext } from './CodeBlock';
import ResponsiveTable from './ResponsiveTable';
import { AnswerPartBoundary, ChecklistBox, ChecklistItem, renderFencePart } from './AnswerPart';
import { isAnswerPartsEnabled } from '../../utils/chatRichCatalog';
import { preprocessMarkdownMath } from './markdownMath';
import { closeIncompleteMarkdown, splitMarkdownBlocks } from './markdownBlocks';
import { isMergeMarkerCell, renderCellBreaks } from './tableCellBreaks';
import remarkReading, { CALLOUT_LABELS, getLinkHost, replaceCalloutMarkers } from './remarkReading';
import remarkAnswerParts, { isStepsContextBlock } from './remarkAnswerParts';

const CalloutIcon = ({ type }) => {
  if (type === 'warning' || type === 'caution') {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (type === 'summary') {
    return (
      <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const hasClass = (className, name) => (
  Array.isArray(className) ? className.includes(name) : String(className || '').split(/\s+/).includes(name)
);

// 블록 하나를 그린다. 본문·플러그인·컴포넌트가 같으면 다시 파싱하지 않는다.
// 그리다 실패하면 그 블록만 기호 없는 글로 보인다(답변 전체가 비지 않게).
const MarkdownBlock = React.memo(({ content, remarkPlugins, rehypePlugins, components }) => (
  <AnswerPartBoundary fallbackText={() => replaceCalloutMarkers(content)} partId="markdown" resetKey={content}>
    <ReactMarkdown
      remarkPlugins={remarkPlugins}
      rehypePlugins={rehypePlugins}
      components={components}
    >
      {content}
    </ReactMarkdown>
  </AnswerPartBoundary>
));

const getTextFromChildren = (children) => React.Children.toArray(children)
  .map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (React.isValidElement(child)) return getTextFromChildren(child.props.children);
    return '';
  })
  .join('');

/**
 * 교육용 마크다운 강화 컴포넌트
 * OpenAI의 마크다운 결과물을 랜딩페이지 쇼케이스처럼 변환
 */
const EnhancedMarkdown = ({
  content,
  enableMath = true,
  className = "",
  onContentProcessed = null,
  contentCategory = null,
  onInternalLink = null,
  streaming = false,
  citations = null,
  onCitationClick = null,
}) => {
  const onInternalLinkRef = useRef(onInternalLink);
  onInternalLinkRef.current = onInternalLink;
  const onCitationClickRef = useRef(onCitationClick);
  onCitationClickRef.current = onCitationClick;

  const partsEnabled = isAnswerPartsEnabled() && String(contentCategory || '').toLowerCase() !== 'record';

  // 앞 블록이 '수업 진행 순서' 같은 제목이면 다음 번호 목록을 단계로 그린다 — 블록마다 따로 파싱하므로 두 벌을 둔다.
  const [remarkPlugins, stepsRemarkPlugins] = useMemo(() => {
    // `**"인용"**를`·`**양분(녹말)**을` 처럼 문장부호로 끝난 굵게 뒤에 한글이 붙어도 굵게가 되게 한다.
    // 취소선은 `~~` 만 — "5~6학년 … 9:00~" 처럼 범위 물결표 두 개 사이가 취소선이 되지 않게.
    const plugins = [[remarkGfm, { singleTilde: false }], remarkCjkFriendly];
    if (enableMath) {
      plugins.push(remarkMath);
    }
    plugins.push([remarkReading, { citations: Array.isArray(citations) ? citations : [] }]);
    if (!partsEnabled) return [plugins, plugins];
    return [
      [...plugins, [remarkAnswerParts, { stepsContext: false }]],
      [...plugins, [remarkAnswerParts, { stepsContext: true }]],
    ];
  }, [enableMath, citations, partsEnabled]);

  const rehypePlugins = useMemo(() => {
    const plugins = [];
    if (enableMath) {
      plugins.push(rehypeKatex);
    }
    return plugins;
  }, [enableMath]);

  const components = useMemo(() => ({
    h1: ({ children, ...props }) => (
      <h1 className="section-header" {...props}>{children}</h1>
    ),
    h2: ({ children, ...props }) => (
      <h2 {...props}>{children}</h2>
    ),
    h3: ({ children, ...props }) => (
      <h3 {...props}>{children}</h3>
    ),
    table: ({ children, ...props }) => (
      <ResponsiveTable {...props}>{children}</ResponsiveTable>
    ),
    pre: ({ children, node, ...props }) => {
      const codeElement = React.Children.toArray(children).find(React.isValidElement);
      // 아래 code 를 덮어쓰면 자식의 type 은 'code' 가 아닌 그 컴포넌트가 된다 — hast 태그명으로도 확인한다.
      if (codeElement?.type === 'code' || codeElement?.props?.node?.tagName === 'code') {
        const className = codeElement.props.className || '';
        const language = (className.match(/language-([\w-]+)/)?.[1]) || 'code';
        const code = getTextFromChildren(codeElement.props.children).replace(/\n$/, '');
        const isRecordPlain = String(contentCategory || '').toLowerCase() === 'record' && !className;
        return renderFencePart({ language, code, forceRecordText: isRecordPlain });
      }

      return <pre {...props}>{children}</pre>;
    },
    code: ({ children, className, ...props }) => (
      String(contentCategory || '').toLowerCase() === 'record' && !className
        ? <span className="em-record-plain-text" {...props}>{children}</span>
        : <code className={className} {...props}>{children}</code>
    ),
    td: ({ children, node, ...props }) => (
      <td {...props}>{isMergeMarkerCell(children) ? null : renderCellBreaks(children)}</td>
    ),
    th: ({ children, node, ...props }) => (
      <th scope="col" {...props}>{isMergeMarkerCell(children) ? null : renderCellBreaks(children)}</th>
    ),
    blockquote: ({ children, node, className, ...props }) => {
      const type = props['data-callout'];
      if (hasClass(className, 'wq-callout') && CALLOUT_LABELS[type]) {
        const summaryCard = partsEnabled && type === 'summary' ? ' wq-part-summary' : '';
        return (
          <blockquote className={`wq-callout wq-callout--${type}${summaryCard}`} data-callout={type}>
            <span className="wq-callout__label">
              <CalloutIcon type={type} />
              {props['data-callout-title'] || CALLOUT_LABELS[type]}
            </span>
            {children}
          </blockquote>
        );
      }
      return <blockquote className="enhanced-blockquote">{children}</blockquote>;
    },
    ...(partsEnabled ? {
      li: ({ children, node, className, ...props }) => (
        hasClass(className, 'task-list-item')
          ? <ChecklistItem className={className} node={node}>{children}</ChecklistItem>
          : <li className={className} {...props}>{children}</li>
      ),
      input: ({ node, type, checked, ...props }) => (
        type === 'checkbox'
          ? <ChecklistBox checked={checked} />
          : <input checked={checked} type={type} {...props} />
      ),
    } : {}),
    // 모델이 쓴 마크다운 이미지는 불러오지 않는다(외부 주소로 대화가 새지 않게). 링크로만 남긴다.
    img: ({ src, alt }) => {
      const host = getLinkHost(src || '');
      const label = String(alt || '').trim() || host || '이미지';
      if (!host) return <span>{label}</span>;
      return (
        <a href={src} target="_blank" rel="noopener noreferrer nofollow">{label}</a>
      );
    },
    a: ({ href, children, node, className, ...props }) => {
      const isCite = hasClass(className, 'wq-cite');
      if (isCite || hasClass(className, 'wq-source-chip')) {
        const host = getLinkHost(href || '');
        return (
          <a
            aria-label={isCite ? `출처 ${getTextFromChildren(children)}: ${props.title || host}` : `출처: ${host}`}
            className={isCite ? 'wq-chip wq-chip--cite' : 'wq-chip wq-chip--source'}
            href={href}
            onClick={() => onCitationClickRef.current?.(isCite ? 'inline_number' : 'inline_domain')}
            rel="noopener noreferrer"
            target="_blank"
            title={props.title || href}
          >
            {children}
          </a>
        );
      }
      const isInternal = Boolean(href && href.startsWith('/'));
      const canHandoff = Boolean(
        isInternal
        && /^\/chat\?[^#]*category=(ppt_maker|worksheet_gen|image_generation|document)(?:&|$)/.test(href)
      );
      // 🧠 AI 기억 관리 링크(/chat/settings#ai-memory 등)는 전체 새로고침 대신
      //    SPA 내부 이동으로 처리한다. 콜백이 없는 화면(공유 뷰 등)은 일반 링크로 둔다.
      const isChatSettingsLink = Boolean(
        isInternal && /^\/chat\/settings(?:[?#]|$)/.test(href)
      );
      return (
        <a
          {...props}
          className={className}
          href={href}
          target={isInternal ? undefined : '_blank'}
          rel={isInternal ? undefined : 'noopener noreferrer'}
          onClick={(canHandoff || isChatSettingsLink) ? (event) => {
            if (isChatSettingsLink && !onInternalLinkRef.current) return;
            event.preventDefault();
            onInternalLinkRef.current?.(href, getTextFromChildren(children));
          } : undefined}
          style={isInternal ? { color: 'var(--wq-accent-text, #3182F6)', textDecoration: 'none', fontWeight: 500 } : undefined}
        >
          {children}
        </a>
      );
    },
  }), [contentCategory, partsEnabled]);

  const processedContent = useMemo(() => {
    if (!content) return '';
    return enableMath ? preprocessMarkdownMath(content) : content;
  }, [content, enableMath]);

  useEffect(() => {
    if (onContentProcessed) {
      onContentProcessed(processedContent);
    }
  }, [processedContent, onContentProcessed]);

  // 최상위 블록마다 따로 그린다. 스트리밍 중에는 마지막 블록만 바뀌어 앞 블록은 다시 파싱하지 않고,
  // 답변이 끝나도 같은 구조라 DOM 이 다시 만들어지지 않는다.
  // components 가 새 prop 을 읽게 되면 ref 로 읽거나 의존성에 넣어야 한다.
  const blocks = useMemo(() => {
    const parts = splitMarkdownBlocks(processedContent);
    if (streaming && parts.length > 0) {
      parts[parts.length - 1] = closeIncompleteMarkdown(parts[parts.length - 1]);
    }
    return parts;
  }, [processedContent, streaming]);

  if (!processedContent) {
    return <div className={`enhanced-education-content ${className}`} />;
  }

  return (
    <div className={`enhanced-education-content ${className}`}>
      <MarkdownStreamingContext.Provider value={streaming === true}>
        {blocks.map((block, index) => (
          <MarkdownBlock
            key={index}
            components={components}
            content={block}
            rehypePlugins={rehypePlugins}
            remarkPlugins={partsEnabled && index > 0 && isStepsContextBlock(blocks[index - 1])
              ? stepsRemarkPlugins
              : remarkPlugins}
          />
        ))}
      </MarkdownStreamingContext.Provider>
    </div>
  );
};

export default EnhancedMarkdown;
